import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from "react-redux";
import Loader from '../../../components/Loader.js';
import { updateTitle } from '../../../helpers/titleHelper.js';
import { getAllReviews, updateReviewStatus, UpdateSchoolVerified } from '../../../api/reviewApi.js';
import { reviewStatusMap, schoolVerificationMap } from '../../../helpers/reviewHelper.js';

export default function ReviewsPage() {
    updateTitle('Admin', 'Reviews');
    const navigate = useNavigate();
    const { siteUserId } = useSelector((state) => state.auth);
    const [loadingData, setLoadingData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [reviews, setReviews] = useState([]);
    const [filter, setFilter] = useState({ start: 1, length: 100, sort: 1, siteUserId });

    const loadMoreRef = useRef(null);

    const fetchReviews = useCallback(async (append = false) => {
        if (append) setLoadingMore(true);
        else setLoadingData(true);

        try {
            const response = await getAllReviews(filter);
            const data = response.data.data || [];
            const totalRecords = response.data.totalCount || 0;

            setTotal(totalRecords);
            setReviews(prev => append ? [...prev, ...data] : data);
        } catch (err) {
            console.error('Error fetching reviews:', err);
        } finally {
            if (append) setLoadingMore(false);
            else setLoadingData(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchReviews(filter.start === 1 ? false : true);
    }, [filter]);

    useEffect(() => {
        if (!loadMoreRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMore && reviews.length < total) {
                    setFilter(prev => ({ ...prev, start: prev.start + prev.length }));
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [reviews, total, loadingMore]);

    const handleStatusChange = (baseId) => async (e) => {
        const newStatus = parseInt(e.target.value, 10);

        try {
            await updateReviewStatus(baseId, newStatus);
            setReviews(prev =>
                prev.map(r => r.baseId === baseId ? { ...r, isApproved: newStatus } : r)
            );
        } catch (err) {
            console.error('Failed to update review status:', err);
        }
    };
    const handleSchoolVerificationChange = (baseId) => async (e) => {
        const newStatus = parseInt(e.target.value, 10);

        try {
            await UpdateSchoolVerified(baseId, newStatus);
            setReviews(prev =>
                prev.map(r => r.baseId === baseId ? { ...r, isSchoolVerified: newStatus } : r)
            );
        } catch (err) {
            console.error('Failed to update school verification:', err);
        }
    };

    const handleSort = (columnSortAsc, columnSortDesc) => {
        setFilter(prev => {
            let newSort = prev.sort === columnSortAsc ? columnSortDesc : columnSortAsc;
            return { ...prev, sort: newSort, start: 1 };
        });
    };

    return (
        <div className="admin-page-wrap">
            <div className="admin-top-bar-wrap">
                <div className="admin-top-bar-left">
                    <Link className="admin-top-bar-back" to="/admin/">Back</Link>
                </div>
            </div>

            <div className="admin-page-content">
                <h1 className="admin-page-heading">Reviews</h1>

                {loadingData ? (
                    <Loader />
                ) : (
                    <>
                        <div className="admin-bottom-bar"></div>

                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort(2, 3)} className={filter.sort === 2 ? 'sortable sorted-up' : filter.sort === 3 ? 'sortable sorted-dn' : 'sortable'}> Product </th>
                                        <th onClick={() => handleSort(4, 5)} className={filter.sort === 4 ? 'sortable sorted-up' : filter.sort === 5 ? 'sortable sorted-dn' : 'sortable'}> District </th>
                                        <th onClick={() => handleSort(6, 7)} className={filter.sort === 6 ? 'sortable sorted-up' : filter.sort === 7 ? 'sortable sorted-dn' : 'sortable'}> School </th>
                                        <th onClick={() => handleSort(8, 9)} className={filter.sort === 8 ? 'sortable sorted-up' : filter.sort === 9 ? 'sortable sorted-dn' : 'sortable'}> Verified School </th>
                                        <th className="admin-table-action-cell"> Rating </th>
                                        <th className="admin-table-action-cell"> Review Status </th>
                                        <th className="admin-table-action-cell"> View </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reviews.length > 0 ? (
                                        reviews.map((review) => (
                                            <tr key={review.id} className="clickable-row">
                                                <td className="admin-table-name-cell space-nowrap">{review.productName}</td>
                                                <td className="admin-table-name-cell space-nowrap">{review.districtName}</td>
                                                <td className="admin-table-name-cell">{review.schoolName || "My Project"}</td>
                                                <td className="admin-table-name-cell">
                                                    <div className="form-group">
                                                        <div className="cart-side-input cart-select-wrapper arrow-button">
                                                            <select
                                                                name="isSchoolVerified"
                                                                id="isSchoolVerified"
                                                                className="cart-grid-select cart-select-arrow"
                                                                value={review.isSchoolVerified}
                                                                onChange={handleSchoolVerificationChange(review.baseId)}
                                                            >
                                                                <option value={0}>---</option>
                                                                {Object.entries(schoolVerificationMap).map(([key, value]) => (
                                                                    <option key={key} value={key}>{value}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="admin-table-name-cell">{review.averageRating}</td>
                                                <td className="admin-table-name-cell">
                                                    <div className="form-group">
                                                        <div className="cart-side-input cart-select-wrapper arrow-button">
                                                            <select name="isApproved" id="isApproved"
                                                                className="cart-grid-select cart-select-arrow"
                                                                value={review.isApproved}
                                                                onChange={handleStatusChange(review.baseId)}
                                                            >
                                                                <option value={0}>---</option>
                                                                {Object.entries(reviewStatusMap).map(([key, value]) => (
                                                                    <option key={key} value={key}>{value}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="admin-table-name-cell">

                                                    <div onClick={() => navigate(`/admin/reviews/details/${review?.baseId}`)}>
                                                        <span className="material-symbols-rounded">
                                                            visibility
                                                        </span>
                                                    </div>

                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="admin-table-name-cell">No active reviews</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Infinite scroll trigger */}
                        {reviews.length < total && (
                            <div ref={loadMoreRef} style={{ height: '50px', marginTop: '1rem' }}>
                                {loadingMore && <Loader />}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
